import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { fetchCommentInbox, postProjectComment } from "./plugins/commentsApi";
import CommentAuthorDisplay from "./plugins/CommentAuthorDisplay";
import CommentMentionTextarea from "./plugins/CommentMentionTextarea";
import { canAccessMessages } from "./plugins/messagesAccess";
import "./plugins/commentInbox.css";

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
  } catch {
    return String(iso);
  }
}

function commentDepth(comment, byId) {
  let depth = 0;
  let parentId = comment.parentId;
  while (parentId && byId.has(parentId) && depth < 8) {
    depth += 1;
    parentId = byId.get(parentId).parentId;
  }
  return depth;
}

function MentionThreadCard({ thread, onThreadUpdated }) {
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const byId = useMemo(
    () => new Map((thread.comments || []).map((c) => [c.id, c])),
    [thread.comments]
  );

  const pid = thread.projectId;
  const wo = thread.workOrder ? String(thread.workOrder) : "";
  const pname = thread.projectName ? String(thread.projectName) : "";
  const title = [wo, pname].filter(Boolean).join(" · ") || `Project #${pid}`;

  const submitReply = async () => {
    const body = replyBody.trim();
    if (!body || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await postProjectComment(pid, { body, parentId: thread.replyParentId });
      setReplyBody("");
      setReplyOpen(false);
      onThreadUpdated();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || "Could not post reply.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <li className="comment-inbox-thread-card">
      <div className="comment-inbox-thread-head">
        <div className="comment-inbox-thread-meta">
          <Link to={`/project/${pid}`} className="comment-inbox-project-link">
            {title}
          </Link>
          {thread.replyCountAfterMention > 0 ? (
            <span className="comment-inbox-thread-badge">
              {thread.replyCountAfterMention} new{" "}
              {thread.replyCountAfterMention === 1 ? "reply" : "replies"}
            </span>
          ) : null}
        </div>
        <span className="comment-inbox-when">Updated {formatWhen(thread.latestActivityAt)}</span>
      </div>

      <div className="comment-inbox-thread-messages">
        {(thread.comments || []).map((comment) => {
          const depth = commentDepth(comment, byId);
          return (
            <article
              key={comment.id}
              className={`comment-inbox-thread-message${comment.youWereMentioned ? " is-mentioned" : ""}`}
              style={{ marginLeft: Math.min(depth, 6) * 12 }}
            >
              <div className="comment-inbox-thread-message-head">
                <CommentAuthorDisplay
                  email={comment.authorEmail}
                  authorProfile={comment.authorProfile}
                  compact
                />
                <div className="comment-inbox-thread-message-meta">
                  {comment.youWereMentioned ? (
                    <span className="comment-inbox-thread-message-tag">You were mentioned</span>
                  ) : null}
                  <span className="comment-inbox-when">{formatWhen(comment.createdAt)}</span>
                </div>
              </div>
              <p className="comment-inbox-thread-message-body">{comment.body || "—"}</p>
            </article>
          );
        })}
      </div>

      <div className="comment-inbox-thread-reply">
        {!replyOpen ? (
          <button
            type="button"
            className="comment-inbox-thread-reply-toggle"
            onClick={() => setReplyOpen(true)}
          >
            Reply in thread
          </button>
        ) : (
          <div className="comment-inbox-thread-reply-form">
            <CommentMentionTextarea
              rows={3}
              placeholder="Write a reply… Type @ to mention someone."
              value={replyBody}
              onChange={setReplyBody}
            />
            {error ? (
              <p className="user-profile-settings-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="comment-inbox-thread-reply-actions">
              <button
                type="button"
                className="user-profile-settings-primary"
                onClick={submitReply}
                disabled={submitting || !replyBody.trim()}
              >
                {submitting ? "Posting…" : "Post reply"}
              </button>
              <button
                type="button"
                className="comment-inbox-thread-reply-cancel"
                onClick={() => {
                  setReplyOpen(false);
                  setReplyBody("");
                  setError("");
                }}
              >
                Cancel
              </button>
              <Link to={`/project/${pid}`} className="comment-inbox-thread-view-link">
                Open project
              </Link>
            </div>
          </div>
        )}
      </div>
    </li>
  );
}

export function CommentInboxPage() {
  const [tab, setTab] = useState("mentioned");
  const [data, setData] = useState({ authored: [], mentioned: [], mentionThreads: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    setError("");
    setLoading(true);
    fetchCommentInbox()
      .then((d) => {
        setData({
          authored: Array.isArray(d.authored) ? d.authored : [],
          mentioned: Array.isArray(d.mentioned) ? d.mentioned : [],
          mentionThreads: Array.isArray(d.mentionThreads) ? d.mentionThreads : [],
        });
      })
      .catch((err) => {
        setError(err?.response?.data?.error || err?.message || "Could not load messages.");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const authoredList = data.authored;
  const mentionThreads = data.mentionThreads;

  return (
    <div className="plugins-page comment-inbox-page">
      <div className="plugins-page-header">
        <h1 className="plugins-page-title">Messages</h1>
        <p className="plugins-page-lead">
          Comments you posted and @mention threads, including replies from your teammates.
        </p>
      </div>

      <div className="comment-inbox-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "mentioned"}
          className={`comment-inbox-tab${tab === "mentioned" ? " is-active" : ""}`}
          onClick={() => setTab("mentioned")}
        >
          Mention threads ({mentionThreads.length})
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "authored"}
          className={`comment-inbox-tab${tab === "authored" ? " is-active" : ""}`}
          onClick={() => setTab("authored")}
        >
          My comments ({authoredList.length})
        </button>
      </div>

      {loading ? <p className="email-triggers-muted">Loading…</p> : null}
      {error ? (
        <p className="user-profile-settings-error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !error && tab === "mentioned" && mentionThreads.length === 0 ? (
        <p className="email-triggers-muted">No mention threads yet.</p>
      ) : null}

      {!loading && !error && tab === "authored" && authoredList.length === 0 ? (
        <p className="email-triggers-muted">No comments posted yet.</p>
      ) : null}

      {tab === "mentioned" ? (
        <ul className="comment-inbox-thread-list">
          {mentionThreads.map((thread) => (
            <MentionThreadCard
              key={`${thread.projectId}-${thread.threadRootId}`}
              thread={thread}
              onThreadUpdated={load}
            />
          ))}
        </ul>
      ) : (
        <ul className="comment-inbox-list">
          {authoredList.map((row) => {
            const pid = row.project_id;
            const wo = row.work_order ? String(row.work_order) : "";
            const pname = row.project_name ? String(row.project_name) : "";
            const title = [wo, pname].filter(Boolean).join(" · ") || `Project #${pid}`;
            return (
              <li key={`authored-${row.id}`} className="comment-inbox-card">
                <div className="comment-inbox-card-meta">
                  <Link to={`/project/${pid}`} className="comment-inbox-project-link">
                    {title}
                  </Link>
                  <span className="comment-inbox-when">{formatWhen(row.created_at)}</span>
                </div>
                <p className="comment-inbox-mention-note">
                  Posted as <strong>{row.author_email}</strong>
                </p>
                <blockquote className="comment-inbox-snippet">{row.body || "—"}</blockquote>
              </li>
            );
          })}
        </ul>
      )}

      <p className="plugins-page-footer">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
      </p>
    </div>
  );
}

export default function CommentInboxGate() {
  if (!canAccessMessages()) {
    return <Navigate to="/" replace />;
  }
  return <CommentInboxPage />;
}
